import { useGetWarrantyProvidersQuery } from '@/services/warranties';
import { Box, Button, FormikSelectInputNative, FormikTextInput, Modal } from '@palmetto/palmetto-components';
import { Field, FieldArray, Form, Formik, FormikValues } from 'formik';
import { SyntheticEvent, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { ProgramType, warrantyMap, WarrantyPaymentFrequency, WarrantyType } from 'types';

export const AccessoryWarrantyModal = ({
  isModalOpen,
  closeModal,
  warrantyType,
  onSubmit,
}: {
  isModalOpen: boolean;
  closeModal: (event?: SyntheticEvent) => void;
  warrantyType?: string;
  onSubmit: (values: FormikValues) => void;
}) => {
  const { programType = ProgramType.hvac } = useParams<{ programType: string }>();
  const warranty = warrantyMap[programType as ProgramType][warrantyType as WarrantyType];
  const { data: warrantyProviderResponse } = useGetWarrantyProvidersQuery({});

  const warrantyProviderOptions = useMemo(() => {
    const programTypeProviders = warrantyProviderResponse?.data?.find(
      (item) => item.programType === programType,
    )?.providers;

    const warrantyProviderList = programTypeProviders?.map((p) => p.name) || [];
    return warrantyProviderList.map((element) => {
      return {
        label: element,
        value: element,
      };
    });
  }, [programType, warrantyProviderResponse]);

  return (
    <Modal ariaLabeledBy="createAccessoryWarranty" isOpen={isModalOpen} onDismiss={closeModal} maxWidth="500px">
      <Modal.Header id="createAccessoryWarranty" title={`Add ${warranty}`} onDismiss={closeModal} />
      <Formik
        initialValues={{
          warrantyProvider: '',
          paymentFrequency: WarrantyPaymentFrequency.upfront,
          accessories: [
            {
              minCost: 0,
              maxCost: 0,
              warrantyCost: 0,
            },
          ],
        }}
        onSubmit={({ warrantyProvider, paymentFrequency, accessories }) =>
          onSubmit({
            warrantyProvider,
            paymentFrequency,
            warrantyType: WarrantyType.accessories,
            accessories: accessories,
          })
        }
      >
        {({ isSubmitting, values }) => {
          return (
            <Form>
              <Modal.Body>
                <Box direction="column" gap="md">
                  <Field
                    type="select"
                    component={FormikSelectInputNative}
                    label="Warranty Provider"
                    name="warrantyProvider"
                    id="warrantyProvider"
                    options={warrantyProviderOptions}
                    isRequired
                  />
                  <Field
                    type="select"
                    component={FormikSelectInputNative}
                    label="Payment Frequency"
                    name="paymentFrequency"
                    id="paymentFrequency"
                    options={Object.keys(WarrantyPaymentFrequency).map((key) => ({
                      label: WarrantyPaymentFrequency[key as keyof typeof WarrantyPaymentFrequency],
                      value: key,
                    }))}
                    isRequired
                  />
                  <Box direction="column" gap="md">
                    <FieldArray
                      name="accessories"
                      render={(arrayHelpers) => {
                        return (
                          <Box gap="lg">
                            {values.accessories.map((_warranty: any, index: number) => (
                              <Box gap="lg" key={index}>
                                <Box gap="md" direction="row">
                                  <Field
                                    type="number"
                                    component={FormikTextInput}
                                    label="Min Cost"
                                    name={`accessories.${index}.minCost`}
                                    id={`accessories.${index}.minCost`}
                                    value={values.accessories[index].minCost}
                                  />
                                  <Field
                                    type="number"
                                    component={FormikTextInput}
                                    label="Max Cost"
                                    name={`accessories.${index}.maxCost`}
                                    id={`accessories.${index}.maxCost`}
                                    value={values.accessories[index].maxCost}
                                  />
                                  <Field
                                    type="number"
                                    component={FormikTextInput}
                                    label="Warranty Cost"
                                    name={`accessories.${index}.warrantyCost`}
                                    id={`accessories.${index}.warrantyCost`}
                                    value={values.accessories[index].warrantyCost}
                                  />
                                </Box>
                              </Box>
                            ))}
                            <Button
                              as="button"
                              onClick={() =>
                                arrayHelpers.push({
                                  minCost: 0,
                                  maxCost: 0,
                                  warrantyCost: 0,
                                })
                              }
                              variant="secondary"
                              tone="neutral"
                              size="sm"
                              aria-label="Add Additional Item"
                              iconPrefix="add"
                            >
                              Add Warranty Band
                            </Button>
                          </Box>
                        );
                      }}
                    />
                  </Box>
                </Box>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  as="button"
                  onClick={() => {
                    closeModal();
                  }}
                  variant="secondary"
                  tone="neutral"
                  size="md"
                  isLoading={isSubmitting}
                >
                  Cancel
                </Button>
                <Button size="md" type="submit" isLoading={isSubmitting}>
                  Add Option
                </Button>
              </Modal.Footer>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};
